import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
    useContext
} from 'react';

import { useValidation } from 'gw-portals-validation-react';
import { ViewModelForm } from 'gw-portals-viewmodel-react';
import SgIdGenerator from 'sg-id-generator';
import { TranslatorContext } from '@jutro/locale';
import _ from 'lodash';
import SgSpmConvictionForm from './sg-spm-add-conviction/SgSpmConvictionForm';
import SgSpmConvictionsTable from './sg-spm-convictions-table/SgSpmConvictionsTable';
import metadata from './SgSpmConvictionsDetails.metadata.json5';
import messages from './SgSpmConvictionsDetails.messages';

function SgSpmConvictionsDetails(props) {
    const {
        id,
        modelVM,
        showErrors,
        path,
        updateData,
        onValidate: onValidateParent,
        messageParams,
        adobeIdSuffix
    } = props;

    const translator = useContext(TranslatorContext);

    const {
        isComponentValid,
        onValidate
    } = useValidation(id);

    const commonProps = {
        modelVM,
        showErrors,
        updateData,
        messageParams
    };

    const [isConvictionFormVisible, setIsConvictionFormVisible] = useState(false);
    const [oldConvictionDetails, setOldConvictionDetails] = useState(false);
    const [editingConvictionIndex, setEditingConvictionIndex] = useState(-1);

    useEffect(() => {
        if (onValidateParent) {
            onValidateParent(isComponentValid, id);
        }
    }, [id, isComponentValid, onValidateParent]);

    const createConviction = useCallback(() => {
        setIsConvictionFormVisible(true);
        const newConvictionModel = {
            tempID: `conv${SgIdGenerator.getNextId()}`,
            convictionDate: {}
        };
        if (_.get(modelVM, path).convictions.length) {
            _.get(modelVM, path)
                .convictions
                .value
                .push(newConvictionModel);
        } else {
            _.get(modelVM, path).convictions.value = [newConvictionModel];
        }
        setEditingConvictionIndex(_.get(modelVM, path).convictions.length - 1);
        updateData(modelVM);
    }, [modelVM, path, updateData]);

    useEffect(() => {
        if (_.get(modelVM, path).hasMotorConvictions.value) {
            if (_.get(modelVM, path).convictions.length === 0) {
                createConviction();
            }
        } else {
            setIsConvictionFormVisible(false);
            setEditingConvictionIndex(-1);
            _.get(modelVM, path).convictions.value = [];
            updateData(modelVM);
        }
    }, [_.get(modelVM, path).hasMotorConvictions.value]);

    const onSaveConviction = useCallback(() => {
        setEditingConvictionIndex(-1);
        setIsConvictionFormVisible(false);
        setOldConvictionDetails(null);
        updateData(modelVM);
    }, [modelVM, setEditingConvictionIndex, setIsConvictionFormVisible, setOldConvictionDetails, updateData]);

    const onCancelConviction = useCallback(() => {
        if (oldConvictionDetails) {
            _.get(modelVM, path).convictions.children[editingConvictionIndex].value = oldConvictionDetails;
            setOldConvictionDetails(null);
        } else if (_.get(modelVM, path).convictions.length === 1) {
            _.get(modelVM, path).convictions.value = [];
            _.get(modelVM, path).hasMotorConvictions.value = false;
        } else {
            _.get(modelVM, path)
                .convictions
                .value
                .splice(editingConvictionIndex, 1);
        }
        setEditingConvictionIndex(-1);
        setIsConvictionFormVisible(false);
        setTimeout(() => {
            updateData(modelVM);
        });
    }, [modelVM, updateData, oldConvictionDetails, path, editingConvictionIndex]);

    const onRemoveConviction = useCallback((index) => {
        _.get(modelVM, path)
            .convictions
            .value
            .splice(index, 1);
        if (_.get(modelVM, path).convictions.length === 0) {
            _.get(modelVM, path).hasMotorConvictions.value = false;
        }
        updateData(modelVM);
    }, [modelVM, path, updateData]);

    const onEditConviction = useCallback((index) => {
        setIsConvictionFormVisible(true);
        setOldConvictionDetails(_.clone(_.get(modelVM, path).convictions.value[index]));
        setEditingConvictionIndex(index);
    }, [modelVM, path]);

    const listIsVisible = useMemo(() => {
        return _.get(modelVM, path).convictions.length > 1 || (_.get(modelVM, path).convictions.length === 1 && !isConvictionFormVisible);
    }, [_.get(modelVM, path).convictions.length, isConvictionFormVisible, path]);

    const overrides = {
        hasConvictions: {
            path: `${path}.hasMotorConvictions`,
            visible: !listIsVisible && !oldConvictionDetails,
            label: translator(messages.HasConvictionsLabel, messageParams),
            adobeID: `hasConvictions${adobeIdSuffix}`,
        },
        spmConvictionsTable: {
            visible: listIsVisible,
            convictions: _.get(modelVM, path).convictions.value,
            onRemoveConviction: onRemoveConviction,
            onEditConviction: onEditConviction,
            editingConvictionIndex: editingConvictionIndex,
            isConvictionFormVisible
        },
        spmConvictionForm: {
            visible: isConvictionFormVisible,
            path: `${path}.convictions.children[${editingConvictionIndex}]`,
            ...commonProps,
            onCancelConviction,
            onSaveConviction,
            onValidate: onValidate,
        },
        addNewConviction: {
            visible: !isConvictionFormVisible && _.get(modelVM, path).convictions.length > 0
        }
    };

    const resolvers = {
        resolveCallbackMap: {
            onAddNewConviction: createConviction
        },
        resolveComponentMap: {
            SgSpmConvictionForm: SgSpmConvictionForm,
            SgSpmConvictionsTable: SgSpmConvictionsTable
        },
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            model={modelVM}
            showErrors={showErrors}
            onModelChange={updateData}
            onValidationChange={onValidate}
            overrideProps={overrides}
            callbackMap={resolvers.resolveCallbackMap}
            componentMap={resolvers.resolveComponentMap}
        />
    );
}

export default SgSpmConvictionsDetails;
